/**
 * useJourney Hook
 * ===============
 * 
 * Hook React per accedere al Journey Data Model consolidato.
 * Source of truth unica per header, timeline, mappa, chat shell.
 * 
 * Il hook carica i dati dall'endpoint /api/journey/{code} che
 * restituisce il modello Journey consolidato con:
 * - Dati base (code, title, status, dates, group, base)
 * - Elementi già raggruppati per giorno
 * - Elementi filtrati per mappa  
 * - Summary per header
 * - Context per chat
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== STATUS CONFIGURATION ==============

// Journey Status (container level)
export const JOURNEY_STATUS = {
  draft: { 
    key: 'draft',
    label: 'Bozza', 
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    description: 'Il tuo viaggio è in fase di definizione'
  },
  confirmed: { 
    key: 'confirmed',
    label: 'Confermato', 
    color: 'bg-green-100 text-green-800 border-green-200',
    description: 'Il tuo viaggio è confermato'
  },
  active: { 
    key: 'active',
    label: 'In Corso', 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    description: 'Buon viaggio in Umbria!'
  },
  completed: { 
    key: 'completed',
    label: 'Completato', 
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    description: 'Grazie per aver viaggiato con noi'
  },
  cancelled: { 
    key: 'cancelled',
    label: 'Annullato', 
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    description: 'Viaggio annullato'
  }
};

// Element Status (item level)
export const ELEMENT_STATUS = {
  draft: { 
    key: 'draft',
    label: 'Bozza', 
    color: 'bg-slate-100 text-slate-600',
    description: 'Da definire'
  },
  proposed: { 
    key: 'proposed',
    label: 'Proposto', 
    color: 'bg-amber-50 text-amber-700',
    description: 'Proposta da confermare'
  },
  verifying: { 
    key: 'verifying',
    label: 'In verifica', 
    color: 'bg-blue-50 text-blue-700',
    description: 'Stiamo verificando disponibilità'
  },
  confirmed: { 
    key: 'confirmed',
    label: 'Confermato', 
    color: 'bg-green-50 text-green-700',
    description: 'Prenotazione confermata'
  },
  pending_change: { 
    key: 'pending_change',
    label: 'Modifica in corso', 
    color: 'bg-orange-50 text-orange-700',
    description: 'Richiesta modifica in elaborazione'
  },
  cancelled: { 
    key: 'cancelled',
    label: 'Annullato', 
    color: 'bg-red-50 text-red-600',
    description: 'Elemento annullato'
  }
};

// Element Types
export const ELEMENT_TYPES = {
  accommodation: { key: 'accommodation', label: 'Alloggio', icon: 'Home' },
  restaurant: { key: 'restaurant', label: 'Ristorante', icon: 'Utensils' },
  winery: { key: 'winery', label: 'Cantina', icon: 'Wine' },
  experience: { key: 'experience', label: 'Esperienza', icon: 'Camera' },
  activity: { key: 'activity', label: 'Attività', icon: 'Bike' },
  wellness: { key: 'wellness', label: 'Benessere', icon: 'Heart' },
  guide: { key: 'guide', label: 'Guida', icon: 'Users' },
  transport: { key: 'transport', label: 'Trasporto', icon: 'Navigation' },
  coffee: { key: 'coffee', label: 'Caffè/Bar', icon: 'Coffee' },
  other: { key: 'other', label: 'Altro', icon: 'MapPin' }
};

// ============== HOOK ==============

/**
 * useJourney - Hook per accedere al Journey Data Model
 * 
 * @param {string} code - Codice del viaggio
 * @returns {Object} - Journey data e helpers
 */
export function useJourney(code) {
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load journey data
  const loadJourney = useCallback(async () => {
    if (!code) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/journey/${code}`);
      
      if (!res.ok) {
        if (res.status === 404) {
          setError('Viaggio non trovato');
        } else {
          setError('Errore nel caricamento');
        }
        setJourney(null);
        return;
      }
      
      const data = await res.json();
      setJourney(data);
    } catch (err) {
      console.error('Failed to load journey:', err);
      setError('Errore di connessione');
      setJourney(null);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    loadJourney();
  }, [loadJourney]);

  // ============== DERIVED DATA ==============
  
  // Journey status config
  const statusConfig = useMemo(() => {
    if (!journey) return JOURNEY_STATUS.draft;
    return JOURNEY_STATUS[journey.status] || JOURNEY_STATUS.draft;
  }, [journey]);

  // Elements by day (from API response)
  const elementsByDay = useMemo(() => {
    if (!journey?.elements_by_day) return {};
    return journey.elements_by_day;
  }, [journey]);

  // Map elements (from API response)
  const mapElements = useMemo(() => {
    if (!journey?.map_elements) return [];
    return journey.map_elements;
  }, [journey]);

  // Summary (from API response)
  const summary = useMemo(() => {
    if (!journey?.summary) {
      return { days: 0, experiences: 0, base: '-', code: code };
    }
    return journey.summary;
  }, [journey, code]);

  // Chat context (from API response)
  const chatContext = useMemo(() => {
    if (!journey?.chat_context) return null;
    return journey.chat_context;
  }, [journey]);

  // Map command for InteractiveMap component
  const mapCommand = useMemo(() => {
    if (!journey) return null;
    
    return {
      mode: journey.status === 'draft' ? 'draft' : 'operational',
      base: journey.base || journey.accommodation_area,
      stops: mapElements.map((el, idx) => ({
        name: el.title,
        location: el.location,
        day: el.day,
        type: el.type
      })),
      intro: `Il tuo viaggio: ${journey.title}`
    };
  }, [journey, mapElements]);

  // Support info
  const supportInfo = useMemo(() => {
    if (!journey?.support) {
      return {
        email: 'concierge@feelumbria.it',
        phone: null,
        hours: 'Lun-Ven 9:00-18:00'
      };
    }
    return journey.support;
  }, [journey]);

  // ============== HELPERS ==============

  // Get element status config
  const getElementStatus = useCallback((status) => {
    return ELEMENT_STATUS[status] || ELEMENT_STATUS.proposed;
  }, []);

  // Get element type config
  const getElementType = useCallback((type) => {
    return ELEMENT_TYPES[type] || ELEMENT_TYPES.other;
  }, []);

  // Format date range for display
  const formatDateRange = useCallback(() => {
    if (!journey?.dates?.start) return '';
    
    const start = new Date(journey.dates.start);
    const end = journey.dates.end ? new Date(journey.dates.end) : null;
    
    const formatOpts = { day: 'numeric', month: 'short' };
    
    if (!end) {
      return start.toLocaleDateString('it-IT', formatOpts);
    }
    
    if (start.getMonth() === end.getMonth()) {
      return `${start.getDate()} - ${end.toLocaleDateString('it-IT', formatOpts)}`;
    }
    
    return `${start.toLocaleDateString('it-IT', formatOpts)} - ${end.toLocaleDateString('it-IT', formatOpts)}`;
  }, [journey]);

  // Format group for display
  const formatGroup = useCallback(() => {
    if (!journey?.group) return '2 persone';
    
    const parts = [];
    const adults = journey.group.adults || 2;
    const children = journey.group.children || 0;
    
    parts.push(`${adults} adult${adults > 1 ? 'i' : 'o'}`);
    
    if (children > 0) {
      parts.push(`${children} bambin${children > 1 ? 'i' : 'o'}`);
    }
    
    return parts.join(', ');
  }, [journey]);

  // Get date for specific day number
  const getDayDate = useCallback((dayNumber) => {
    if (!journey?.dates?.start || !dayNumber) return null;
    
    const start = new Date(journey.dates.start);
    const dayDate = new Date(start);
    dayDate.setDate(start.getDate() + dayNumber - 1);
    return dayDate;
  }, [journey]);

  return {
    // Raw data
    journey,
    loading,
    error,
    
    // Status
    statusConfig,
    
    // Derived data (from source of truth)
    elementsByDay,
    mapElements,
    summary,
    chatContext,
    mapCommand,
    supportInfo,
    
    // Helpers
    getElementStatus,
    getElementType,
    formatDateRange,
    formatGroup,
    getDayDate,
    
    // Actions
    reload: loadJourney
  };
}

export default useJourney;
